/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid, react/self-closing-comp */
import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import ParcelCreationPriceBlock from './parcel-creation-price.component';
import {
  ProgressButtonContainer,
  ParcelCreationBottomContainer,
  Recaptcha,
  PayButtonSupportText,
} from './parcel-creation-bottom.styles';
import Button from '../../../common/button/button.component';
import { store, useStore } from '../../../stores/store';
import { ReactComponent as AirplaneIcon } from '../../../assets/airplane.svg';
import { ReactComponent as ShipIcon } from '../../../assets/ship.svg';
import { ReactComponent as RocketIcon } from '../../../assets/rocket.svg';
import { ReactComponent as CarIcon } from '../../../assets/car.svg';
import ReCAPTCHA from '../../../common/recaptcha/recaptcha.component';
import { TRecaptchaToken } from '../../../stores/parcelCreationFlowStore';
import { generateMyRoute } from '../../../utils/uri/uri.utils';
import { AppRoute } from '../../../constants/route';
import { handlePaymentRedirect } from '../../../utils/payments/payments.utils';
import { getGeocodingInfo } from '../../../utils/google-places/google-places.utils';
import { toGeocode } from '../../../common/map/map.helpers';
import { constructGoogleMapsUrl } from '../../../utils/generic/generic.utils';
import { useModal } from '../../../common/modal';
import { Modals } from '../../../constants/modals';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import { scrollToFirstInvalidInput } from '../../../utils/parcel-creation/parcel-creation-utils';
import { mergeLinks } from '../../footer/helpers/utils';
import { DeliveryMethod } from '../../../models/delivery-options/common';
import { FooterLinks } from '../../../models/footerLinksModel';
import { LinkSmall } from '../../../common/typography/typography.styles';
import { MarginContainer } from '../../../common/base-margin/base-margin.component';
import { testIds } from '../../../../test-ids';
import { ParcelHistoryNavigationContext } from '../../../hooks/use-parcel-history-navigation.hook';

export const MiddleMileIcon: React.FC<{
  method: DeliveryMethod | undefined;
}> = ({ method }) => {
  switch (method) {
    case 'air':
      return <AirplaneIcon />;
    case 'car':
      return <CarIcon />;
    case 'sea':
      return <ShipIcon />;
    case 'expedited':
      return <RocketIcon />;
    default:
      return null;
  }
};

const ParcelCreationBottom = () => {
  const {
    parcelCreationStore: {
      selectedDeliveryOption,
      navigation,
      validateCurrentStep,
      proceedToNextStep,
      proceedToPrevStep,
      createParcel,
      guestRecaptchaToken,
      isNextStepButtonLoading,
      setGuestRecaptchaToken,
      getPaymentLink,
      isLoadingShipmentApi,
      isAgentFlowShowMap,
      selectedSenderPudo,
      countryDeparture,
      setIsNextStepButtonLoading,
      isFinishWithPay,
      isSignatureRequired,
      setIsNextButtonClicked,
      isNextButtonClicked,
      setIsSignButtonClicked,
      setIsParcelCreationInProgress,
      prefillInProgress,
      submitSignature,
      resetAllData,
      routeNotFoundError,
    },
    commonStore: { isGuestParcelCreation, toastSuccess },
    localizationsStore: { isLanguageChanging, availableCountries },
    marketingStore: {
      countriesForPayButtonSupportText,
      getHardcodedFooterLinks: getFooterLinks,
    },
    shipmentCountriesStore: { isDirectionsListApiLoading },
  } = useStore();
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] =
    useState<TRecaptchaToken>(guestRecaptchaToken);

  const { openModal } = useModal();

  const isMobile = useIsMobile();

  const { t } = useTranslation();

  const handlePayParcel = async () => {
    const link = getPaymentLink();
    if (link) {
      handlePaymentRedirect(link);
    }
  };

  const handleCreateParcel = async () => {
    setIsNextStepButtonLoading(true);

    // Here we take signature from store, because in case of 'Signature Modal' this function stores
    // incorrect signature value via closure mechanism
    if (store.parcelCreationStore.signature) {
      const response = await fetch(store.parcelCreationStore.signature);
      const blob = await response.blob();
      const file = new File([blob], 'signature.jpeg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('signature', file);

      await submitSignature(formData);
    }

    const success = await createParcel();

    if (!success) {
      setIsNextStepButtonLoading(false);
      return;
    }

    if (isFinishWithPay) {
      await handlePayParcel();
    }

    // Unblock navigation blocker first
    setIsParcelCreationInProgress(false);
    setTimeout(() => {
      navigate(generateMyRoute(AppRoute.PARCEL_LIST));
      resetAllData();
    }, 50);
    toastSuccess(t('new_parcel_has_been_created'));

    setIsNextStepButtonLoading(false);
  };

  const handleProceedToNextStep = async (navigateToStep: NavigateFunction) => {
    setGuestRecaptchaToken(recaptchaToken);
    await proceedToNextStep(navigateToStep);
  };

  const handleNavigateTo = async () => {
    if (!selectedSenderPudo) return;

    setIsNextStepButtonLoading(true);
    const senderZipGeoInfo = await getGeocodingInfo(countryDeparture?.zipCode);
    const senderGeocode = toGeocode(senderZipGeoInfo?.geometry.location);
    window.open(
      constructGoogleMapsUrl(selectedSenderPudo.geocode, senderGeocode),
      '_blank'
    );
    setIsNextStepButtonLoading(false);
  };

  const isFirstStep =
    navigation.current!.name === AppRoute.PARCEL_CREATION_PARCEL_DETAILS;
  const isSecondStep =
    navigation.current!.name === AppRoute.PARCEL_CREATION_DELIVERY_OPTION;
  const isDeliveryInstructionsStep =
    navigation.current!.name === 'delivery-instructions';
  const isSummaryStep =
    navigation.current!.name === AppRoute.PARCEL_CREATION_SUMMARY;
  const isSignatureStep = navigation.current!.name === AppRoute.SIGNATURE;
  const shouldOpenSignatureModal =
    isSummaryStep && isSignatureRequired && !isMobile;
  const isRecaptchaRequired = isFirstStep && isGuestParcelCreation;
  const isRecaptchaValid = !!(recaptchaToken || guestRecaptchaToken);
  const isLastStep = navigation.next === null && navigation.map.length > 2;
  const isSubmitEnabled = isRecaptchaRequired
    ? !!(isRecaptchaValid && validateCurrentStep())
    : !!validateCurrentStep();

  const getSubmitButtonText = () => {
    if (!isLastStep) {
      return t('next_step');
    }

    if (isAgentFlowShowMap) {
      return t('navigate_to');
    }

    if (isFinishWithPay) {
      return t('pay');
    }

    if (isSignatureRequired && isMobile) {
      return t('sign_and_create');
    }

    return t('create_parcel');
  };

  const handleSubmitButtonClick = async (navigateToStep: NavigateFunction) => {
    if (routeNotFoundError) {
      openModal({
        id: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
        name: Modals.UNSUPPORTED_SHIPPING_DIRECTION,
        props: {
          title: routeNotFoundError?.title,
          body: routeNotFoundError?.message,
          showNotifyButton: true,
        },
      });
      return;
    }
    setIsNextButtonClicked(true);
    if (isSignatureStep) setIsSignButtonClicked(true);
    if (!isSubmitEnabled) {
      setTimeout(scrollToFirstInvalidInput, 100);
      return;
    }

    if (!isLastStep && shouldOpenSignatureModal) {
      openModal({
        id: Modals.SIGNATURE,
        name: Modals.SIGNATURE,
        props: {
          handleCreateParcel,
        },
      });
      return;
    }

    if (!isLastStep) {
      await handleProceedToNextStep(navigateToStep);
      return;
    }

    if (isAgentFlowShowMap) {
      await handleNavigateTo();
      return;
    }

    await handleCreateParcel();
  };

  const handleBack = (navigateToStep: NavigateFunction) => {
    proceedToPrevStep(navigateToStep);
    if (isSignatureStep) setIsSignButtonClicked(false);
  };
  const backDisabled =
    isLoadingShipmentApi ||
    isLanguageChanging ||
    isNextStepButtonLoading ||
    isDirectionsListApiLoading;
  const nextDisabled =
    isNextStepButtonLoading ||
    prefillInProgress ||
    isLoadingShipmentApi ||
    isLanguageChanging;
  const historyHandlers = useContext(ParcelHistoryNavigationContext);
  useEffect(() => {
    if (!historyHandlers) return;
    historyHandlers.current = {
      back: handleBack,
      next: handleSubmitButtonClick,
      backDisabled,
      nextDisabled,
    };
    return () => {
      historyHandlers.current = null;
    };
  });

  const showPriceBlock =
    !!selectedDeliveryOption &&
    !isFirstStep &&
    !isSecondStep &&
    !isDeliveryInstructionsStep &&
    !isSignatureStep;

  const showPayButtonSupportText =
    navigation.current?.name === 'summary' &&
    countriesForPayButtonSupportText.includes(countryDeparture?.code ?? '');

  let links: FooterLinks | undefined;

  if (showPayButtonSupportText) {
    const departureLinks = availableCountries?.find(
      (c) => c.country_code === countryDeparture?.code
    )?.links;

    const departureFooterLinks = getFooterLinks(countryDeparture?.code ?? '');

    links = mergeLinks(departureLinks, departureFooterLinks);
  }

  return (
    <ParcelCreationBottomContainer
      $transparentBackground={isFirstStep}
      data-testid={testIds.parceCreation.priceBlock}>
      {showPriceBlock && <ParcelCreationPriceBlock />}
      {isRecaptchaRequired && !guestRecaptchaToken && (
        <Recaptcha id='recaptcha-wrapper'>
          <ReCAPTCHA
            onExpired={() => {
              setRecaptchaToken(null);
            }}
            onChange={setRecaptchaToken}
            isError={isNextButtonClicked.value && !recaptchaToken}
          />
        </Recaptcha>
      )}

      {showPayButtonSupportText && (
        <MarginContainer
          $mt='2.8rem'
          $ml='2.4rem'
          $mr='2.4rem'
          $mtMobile='1.6rem'
          $mlMobile='2.4rem'
          $mrMobile='2.4rem'>
          <PayButtonSupportText>
            <Trans
              i18nKey='pay_button_support_text'
              components={{
                // eslint-disable-next-line
                1: (
                  <LinkSmall
                    underline='hover'
                    target='_blank'
                    rel='noopener noreferrer'
                  />
                ),
                // eslint-disable-next-line
                2: (
                  <LinkSmall
                    underline='hover'
                    target='_blank'
                    rel='noopener noreferrer'
                  />
                ),
              }}
              values={{
                privacyLink: links?.privacy_policy,
                termsLink: links?.terms_of_use,
              }}
            />
          </PayButtonSupportText>
        </MarginContainer>
      )}

      <ProgressButtonContainer
        $mt='0'
        $mtMobile='0'
        $numberOfBtns={isFirstStep ? 1 : 2}>
        {!isFirstStep && (
          <Button
            $pt='1.4rem'
            $pb='1.4rem'
            appearance='secondary'
            disabled={backDisabled}
            onClick={() => handleBack(navigate)}>
            {t('back')}
          </Button>
        )}
        <Button
          $mt='0'
          $pt='1.4rem'
          $pb='1.4rem'
          isLoading={isNextStepButtonLoading || prefillInProgress}
          disabled={nextDisabled}
          onClick={() => handleSubmitButtonClick(navigate)}>
          {getSubmitButtonText()}
        </Button>
      </ProgressButtonContainer>
    </ParcelCreationBottomContainer>
  );
};

export default observer(ParcelCreationBottom);
